// Token utility functions for managing JWT tokens

export const TOKEN_KEY = 'token';
export const USER_KEY = 'user';

/**
 * Get token from localStorage
 */
export const getToken = () => {
  return localStorage.getItem(TOKEN_KEY);
};

/**
 * Get user data from localStorage
 */
export const getUser = () => {
  try {
    const userData = localStorage.getItem(USER_KEY);
    return userData ? JSON.parse(userData) : null;
  } catch (error) {
    console.error('Error parsing user data:', error);
    return null;
  }
};

/**
 * Set token and user data in localStorage
 */
export const setAuthData = (token, user) => {
  localStorage.setItem(TOKEN_KEY, token);
  localStorage.setItem(USER_KEY, JSON.stringify(user));
};

/**
 * Clear authentication data from localStorage
 */
export const clearAuthData = () => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
  localStorage.removeItem('userLimits');
};

/**
 * Cache user limits in localStorage
 */
export const cacheUserLimits = (limits) => {
  localStorage.setItem('userLimits', JSON.stringify(limits));
};

/**
 * Get cached user limits from localStorage
 */
export const getCachedUserLimits = () => {
  try {
    const cachedLimits = localStorage.getItem('userLimits');
    return cachedLimits ? JSON.parse(cachedLimits) : null;
  } catch (error) {
    console.error('Error parsing cached user limits:', error);
    return null;
  }
};

/**
 * Get user limits with fallback to cached values
 */
export const getUserLimits = () => {
  const user = getUser();
  if (user && user.subscription) {
    // Cache the current limits
    const limits = {
      plan: user.subscription.plan || 'free',
      maxProjects: user.subscription.maxProjects,
      maxDocuments: user.subscription.maxDocuments,
      maxShortLinks: user.subscription.maxShortLinks,
      maxInternalAds: user.subscription.maxInternalAds,
      maxSupportModules: user.subscription.maxSupportModules
    };
    cacheUserLimits(limits);
    return limits;
  }
  
  // Fallback to cached limits
  return getCachedUserLimits();
};

/**
 * Check if token exists and is valid
 */
export const isTokenValid = () => {
  const token = getToken();
  if (!token) return false;

  try {
    // Decode token without verification to check expiry
    const payload = JSON.parse(atob(token.split('.')[1]));
    const currentTime = Date.now() / 1000;
    
    // Check if token is expired (with 5 minute buffer)
    const isExpired = payload.exp < (currentTime + 300);
    
    return !isExpired;
  } catch (error) {
    console.error('Error validating token:', error);
    return false;
  }
};

/**
 * Get token expiry time
 */
export const getTokenExpiry = () => {
  const token = getToken();
  if (!token) return null;

  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return new Date(payload.exp * 1000);
  } catch (error) {
    console.error('Error getting token expiry:', error);
    return null;
  }
};

/**
 * Check if token will expire soon (within 1 hour)
 */
export const isTokenExpiringSoon = () => {
  const token = getToken();
  if (!token) return true;

  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const currentTime = Date.now() / 1000;
    const timeUntilExpiry = payload.exp - currentTime;
    
    // Token expires within 1 hour
    return timeUntilExpiry < 3600;
  } catch (error) {
    console.error('Error checking token expiry:', error);
    return true;
  }
};

/**
 * Redirect to login page
 */
export const redirectToLogin = () => {
  console.log('Redirecting to login page');
  clearAuthData();
  window.location.href = '/login';
};

/**
 * Create authenticated fetch headers
 */
export const getAuthHeaders = () => {
  const token = getToken();
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  };
};

/**
 * Handle API response and check for authentication errors
 */
export const handleApiResponse = async (response) => {
  if (response.status === 401) {
    console.log('API returned 401, token may be expired');
    redirectToLogin();
    throw new Error('Authentication failed');
  }
  
  if (response.status === 429) {
    console.log('API returned 429, rate limit exceeded');
    throw new Error('Too many requests. Please wait a moment and try again.');
  }
  
  // Check for successful status codes (200-299)
  if (response.status < 200 || response.status >= 300) {
    const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
    throw new Error(errorData.message || 'API request failed');
  }
  
  return response;
};

/**
 * Get base URL for API endpoints
 * Set REACT_APP_API_URL at build time if the API is not served from the same origin as the SPA (e.g. static CDN + separate API host).
 */
export const getBaseUrl = () => {
  const fromEnv = typeof process !== 'undefined' && process.env.REACT_APP_API_URL;
  if (fromEnv && String(fromEnv).trim()) {
    return String(fromEnv).replace(/\/$/, '');
  }

  const isProduction = window.location.hostname !== 'localhost' &&
    window.location.hostname !== '127.0.0.1' &&
    !window.location.hostname.includes('192.168.');

  if (isProduction) {
    return `${window.location.protocol}//${window.location.hostname}`;
  }

  return `${window.location.protocol}//${window.location.hostname}:3001`;
};

/**
 * Get full API URL
 */
export const getApiUrl = (endpoint) => {
  const baseUrl = getBaseUrl();
  // Remove leading slash if present to avoid double slashes
  const cleanEndpoint = endpoint.startsWith('/') ? endpoint.slice(1) : endpoint;
  return `${baseUrl}/${cleanEndpoint}`;
};

/**
 * Make authenticated API request
 */
export const authenticatedFetch = async (url, options = {}) => {
  // Check token validity before making request
  if (!isTokenValid()) {
    console.log('Token is invalid, redirecting to login');
    redirectToLogin();
    throw new Error('Token is invalid');
  }

  console.log('Making authenticated request to:', url);
  console.log('Base URL:', getBaseUrl());
  console.log('Full URL:', getApiUrl(url));

  const headers = {
    ...getAuthHeaders(),
    ...options.headers
  };

  // Handle body serialization for JSON requests
  let body = options.body;
  if (body && typeof body === 'object' && !(body instanceof FormData)) {
    body = JSON.stringify(body);
    headers['Content-Type'] = 'application/json';
  }

  // Use full URL instead of relative URL
  const fullUrl = getApiUrl(url);
  console.log('Fetching to full URL:', fullUrl);

  const response = await fetch(fullUrl, {
    ...options,
    headers,
    body
  });

  return handleApiResponse(response);
};
